/**
 * Tiny strike zone rendered INSIDE the FieldDiamond SVG (bottom-right of
 * the green field area). Plots the current at-bat's pitches as they come
 * in. Coordinates are mapped into a small ~22×27 box at the right-corner
 * of the 100×100 field viewBox.
 *
 * Returns null when there are no pitches with plate_x/plate_z — between
 * at-bats and on stadiums without Statcast tracking, no zone shows.
 */
import type { PitchEntry } from '../state/selectors';

interface FieldStrikeZoneProps {
  pitches: PitchEntry[];
}

// Position inside the field's 0..100 viewBox. The box sits in the
// bottom-right green area, mostly over the right-field grass.
const ORIGIN_X = 75;
const ORIGIN_Y = 64;
const BOX_W = 22;
const BOX_H = 26;

// Internal horizontal bounds of the strike-zone rect within the box.
const ZONE_LEFT = ORIGIN_X + 4;
const ZONE_RIGHT = ORIGIN_X + BOX_W - 4;

// Statcast plate_x ∈ [-0.85, 0.85] ft (strike zone width = 1.7 ft).
// plate_z ∈ [~1.5, 3.5] ft typically; clamp to [0, 5] for the dot positions.
const PX_MIN = -1.5;
const PX_MAX = 1.5;
const PZ_MIN = 0;
const PZ_MAX = 5;

function mapPxToX(plateX: number): number {
  const c = Math.max(PX_MIN, Math.min(PX_MAX, plateX));
  return ORIGIN_X + ((c - PX_MIN) / (PX_MAX - PX_MIN)) * BOX_W;
}

function mapPzToY(plateZ: number): number {
  const c = Math.max(PZ_MIN, Math.min(PZ_MAX, plateZ));
  // Y increases downward in SVG; high pitches near the top of the box.
  return ORIGIN_Y + BOX_H - ((c - PZ_MIN) / (PZ_MAX - PZ_MIN)) * BOX_H;
}

function pitchFill(p: PitchEntry): string {
  const d = (p.result ?? '').toLowerCase();
  if (d.includes('in play')) return 'var(--c-warn, #f59e0b)';
  if (d.includes('strike') || d.includes('foul')) return 'var(--c-ink-100, #0c111c)';
  if (d.includes('ball')) return 'var(--c-accent, #2563eb)';
  return 'var(--c-ink-70, #3d4456)';
}

export default function FieldStrikeZone({ pitches }: FieldStrikeZoneProps): JSX.Element | null {
  const valid = pitches.filter(
    (p) => typeof p.plateX === 'number' && typeof p.plateZ === 'number',
  );
  if (valid.length === 0) return null;

  // Use the first pitch's sz_top/sz_bot if available; fallback to rule-book.
  const sample = valid[0]!;
  const szTop = typeof sample.szTop === 'number' ? sample.szTop : 3.5;
  const szBot = typeof sample.szBot === 'number' ? sample.szBot : 1.5;
  const zoneTopY = mapPzToY(szTop);
  const zoneBotY = mapPzToY(szBot);

  return (
    <g data-testid="field-strike-zone" pointerEvents="none">
      {/* Frame background */}
      <rect
        x={ORIGIN_X}
        y={ORIGIN_Y}
        width={BOX_W}
        height={BOX_H}
        fill="var(--c-surface, #fff)"
        stroke="var(--c-ink-50, #6c7385)"
        strokeWidth={0.3}
        opacity={0.92}
        rx={1}
      />
      {/* Strike-zone rect (per-batter bounds) */}
      <rect
        x={ZONE_LEFT}
        y={zoneTopY}
        width={ZONE_RIGHT - ZONE_LEFT}
        height={zoneBotY - zoneTopY}
        fill="none"
        stroke="var(--c-ink-80, #2b3142)"
        strokeWidth={0.4}
      />
      {/* Pitches */}
      {valid.map((p, i) => {
        const isRecent = i === valid.length - 1;
        const cx = mapPxToX(p.plateX as number);
        const cy = mapPzToY(p.plateZ as number);
        return (
          <circle
            key={p.playId ?? `${i}`}
            cx={cx}
            cy={cy}
            r={isRecent ? 0.9 : 0.7}
            fill={pitchFill(p)}
            stroke="var(--c-ink-0, #fff)"
            strokeWidth={0.18}
            opacity={isRecent ? 1 : 0.85}
          />
        );
      })}
    </g>
  );
}
